/**
 * Ink Bar — colour picker with CMYK calculation + ink bottle mix recipe.
 * product mode: hidden order properties, no visible HEX/CMYK
 * mixer mode: full preview with CMYK bars and ml quantities
 *
 * Mix formula (from CMYK Ink Mixing Calculator):
 *   base_ml = (channel% / total%) * target_ml
 *   Among C, M, Y — the highest channel gets +10% (×1.1)
 *   Black (K) is always reduced by 30% (×0.7)
 */
(function () {
  'use strict';

  var DEFAULT_TARGET_ML = 5;
  var BOOST_CHANNEL = 1.1;
  var BLACK_FACTOR = 0.7;

  function normalizeHex(value) {
    if (!value) return null;
    var hex = String(value).trim().toUpperCase();
    if (hex.charAt(0) !== '#') hex = '#' + hex;
    if (!/^#([0-9A-F]{6})$/.test(hex)) return null;
    return hex;
  }

  function hexToRgb(hex) {
    var normalized = normalizeHex(hex);
    if (!normalized) return null;
    return {
      r: parseInt(normalized.slice(1, 3), 16),
      g: parseInt(normalized.slice(3, 5), 16),
      b: parseInt(normalized.slice(5, 7), 16)
    };
  }

  function rgbToCmyk(r, g, b) {
    if (r === 0 && g === 0 && b === 0) {
      return { c: 0, m: 0, y: 0, k: 100 };
    }
    var rn = r / 255;
    var gn = g / 255;
    var bn = b / 255;
    var k = 1 - Math.max(rn, gn, bn);
    var c = (1 - rn - k) / (1 - k);
    var m = (1 - gn - k) / (1 - k);
    var y = (1 - bn - k) / (1 - k);
    return {
      c: Math.round(isNaN(c) ? 0 : c * 100),
      m: Math.round(isNaN(m) ? 0 : m * 100),
      y: Math.round(isNaN(y) ? 0 : y * 100),
      k: Math.round(k * 100)
    };
  }

  function formatCmyk(cmyk) {
    return cmyk.c + ', ' + cmyk.m + ', ' + cmyk.y + ', ' + cmyk.k;
  }

  /**
   * Convert CMYK % into bottle ml quantities for a target batch size.
   * @returns {{ c: number, m: number, y: number, k: number, boosted: string, total: number }}
   */
  function cmykToMixMl(cmyk, targetMl) {
    var target = Number(targetMl);
    if (!isFinite(target) || target <= 0) target = DEFAULT_TARGET_ML;

    var totalPct = cmyk.c + cmyk.m + cmyk.y + cmyk.k;
    if (totalPct <= 0) {
      return { c: 0, m: 0, y: 0, k: 0, boosted: null, total: 0 };
    }

    var base = {
      c: (cmyk.c / totalPct) * target,
      m: (cmyk.m / totalPct) * target,
      y: (cmyk.y / totalPct) * target,
      k: (cmyk.k / totalPct) * target
    };

    var boosted = 'c';
    var maxPct = cmyk.c;
    if (cmyk.m > maxPct) {
      maxPct = cmyk.m;
      boosted = 'm';
    }
    if (cmyk.y > maxPct) {
      boosted = 'y';
    }

    var mix = {
      c: base.c,
      m: base.m,
      y: base.y,
      k: base.k * BLACK_FACTOR
    };
    mix[boosted] = base[boosted] * BOOST_CHANNEL;

    return {
      c: mix.c,
      m: mix.m,
      y: mix.y,
      k: mix.k,
      boosted: boosted,
      total: mix.c + mix.m + mix.y + mix.k
    };
  }

  function formatMl(value) {
    if (!isFinite(value) || value <= 0) return '0 ml';
    var rounded = Math.round(value * 100) / 100;
    if (rounded === Math.floor(rounded)) return rounded + ' ml';
    return rounded.toFixed(2).replace(/\.?0+$/, '') + ' ml';
  }

  function parseTargetMl(value) {
    var n = parseFloat(value);
    if (!isFinite(n) || n <= 0) return null;
    return n;
  }

  function bindInkBar(container) {
    if (container.dataset.inkBarBound === 'true') return;
    container.dataset.inkBarBound = 'true';

    var mode = container.dataset.inkBarMode || 'mixer';
    var colorInput = container.querySelector('[data-ink-bar-color]');
    var visibleHex = container.querySelector('[data-ink-bar-hex-visible]');
    var hexText = container.querySelector('[data-ink-bar-hex]');
    var cmykText = container.querySelector('[data-ink-bar-cmyk]');
    var swatch = container.querySelector('[data-ink-bar-swatch]');
    var glow = container.querySelector('[data-ink-bar-glow]');
    var hexHidden = container.querySelector('[data-ink-bar-hex-hidden]');
    var cmykHidden = container.querySelector('[data-ink-bar-cmyk-hidden]');
    var errorEl = container.querySelector('[data-ink-bar-error]');
    var mlInput = container.querySelector('[data-ink-bar-ml]');
    var mlTotalEl = container.querySelector('[data-ink-bar-ml-total]');
    var mlBoostNote = container.querySelector('[data-ink-bar-ml-boost]');

    if (!colorInput || !swatch) return;

    var cmykBars = {
      c: container.querySelector('[data-ink-bar-cmyk-bar-c]'),
      m: container.querySelector('[data-ink-bar-cmyk-bar-m]'),
      y: container.querySelector('[data-ink-bar-cmyk-bar-y]'),
      k: container.querySelector('[data-ink-bar-cmyk-bar-k]')
    };
    var cmykPcts = {
      c: container.querySelector('[data-ink-bar-cmyk-c]'),
      m: container.querySelector('[data-ink-bar-cmyk-m]'),
      y: container.querySelector('[data-ink-bar-cmyk-y]'),
      k: container.querySelector('[data-ink-bar-cmyk-k]')
    };
    var cmykMls = {
      c: container.querySelector('[data-ink-bar-ml-c]'),
      m: container.querySelector('[data-ink-bar-ml-m]'),
      y: container.querySelector('[data-ink-bar-ml-y]'),
      k: container.querySelector('[data-ink-bar-ml-k]')
    };

    var lastCmyk = null;
    var channelNames = { c: 'Cyan', m: 'Magenta', y: 'Yellow', k: 'Black' };

    function showError(message) {
      if (!errorEl) return;
      if (message) {
        errorEl.textContent = message;
        errorEl.classList.add('is-visible');
      } else {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
      }
    }

    function getTargetMl() {
      if (!mlInput) return DEFAULT_TARGET_ML;
      var parsed = parseTargetMl(mlInput.value);
      return parsed == null ? DEFAULT_TARGET_ML : parsed;
    }

    function updateMixUI(cmyk) {
      if (!cmyk) return;

      var targetMl = getTargetMl();
      var mix = cmykToMixMl(cmyk, targetMl);
      var barTotal = mix.total > 0 ? mix.total : 1;

      ['c', 'm', 'y', 'k'].forEach(function (key) {
        var pct = cmyk[key];
        var ml = mix[key];
        var barPct = (ml / barTotal) * 100;

        if (cmykBars[key]) cmykBars[key].style.width = barPct + '%';
        if (cmykPcts[key]) cmykPcts[key].textContent = pct + '%';
        if (cmykMls[key]) {
          cmykMls[key].textContent = formatMl(ml);
          cmykMls[key].classList.toggle('is-boosted', mix.boosted === key);
        }
      });

      if (mlTotalEl) mlTotalEl.textContent = formatMl(mix.total);

      if (mlBoostNote) {
        if (mix.boosted) {
          mlBoostNote.textContent =
            channelNames[mix.boosted] +
            ' is highest among C/M/Y (+10%). Black reduced by 30%.';
        } else {
          mlBoostNote.textContent = '';
        }
      }
    }

    function updateCmykUI(cmyk, cmykString) {
      if (cmykText) cmykText.textContent = cmykString;
      lastCmyk = cmyk;
      updateMixUI(cmyk);
    }

    function update(hex) {
      var normalized = normalizeHex(hex);
      if (!normalized) {
        if (mode === 'mixer') {
          showError('Enter a valid HEX colour, e.g. #1A1A1A');
        }
        return;
      }

      showError('');
      var rgb = hexToRgb(normalized);
      var cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);
      var cmykString = formatCmyk(cmyk);

      colorInput.value = normalized;
      if (visibleHex) visibleHex.value = normalized;
      if (hexText) hexText.textContent = normalized;
      swatch.style.backgroundColor = normalized;
      if (glow) glow.style.backgroundColor = normalized;

      updateCmykUI(cmyk, cmykString);

      if (hexHidden) hexHidden.value = normalized;
      if (cmykHidden) cmykHidden.value = cmykString;
    }

    colorInput.addEventListener('input', function () {
      update(colorInput.value);
    });

    if (visibleHex) {
      visibleHex.addEventListener('change', function () {
        update(visibleHex.value);
      });
      visibleHex.addEventListener('blur', function () {
        update(visibleHex.value);
      });
    }

    if (mlInput) {
      mlInput.addEventListener('input', function () {
        if (lastCmyk) updateMixUI(lastCmyk);
      });
      mlInput.addEventListener('change', function () {
        var parsed = parseTargetMl(mlInput.value);
        if (parsed == null) {
          mlInput.value = String(DEFAULT_TARGET_ML);
        } else {
          mlInput.value = String(parsed);
        }
        if (lastCmyk) updateMixUI(lastCmyk);
      });
    }

    // Clicking swatch trigger on product page opens colour picker
    var swatchTrigger = container.querySelector('.ink-bar__swatch-trigger');
    if (swatchTrigger && mode === 'product') {
      swatchTrigger.addEventListener('click', function () {
        colorInput.click();
      });
    }

    update(visibleHex ? visibleHex.value : colorInput.value);
  }

  function init() {
    document.querySelectorAll('[data-ink-bar]').forEach(bindInkBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
